/**
 * Navigation der Katalogauswahl als reiner Reducer: Pfad durch die Ebenen,
 * Seite je Ebene (Seitenregel) und ein Rücksprung-Stapel, damit „Zurück“
 * aus Ebene 2 auf der Seite der Ebene 1 landet, von der man kam.
 */

export type NavModus = 'torte' | 'suche' | 'zuletzt' | 'favoriten';

export interface NavZustand {
    modus: NavModus;
    /** Geöffnete Knoten von der Kategorie abwärts. */
    pfad: string[];
    /** Seite der aktuellen Ebene (0-basiert). */
    seite: number;
    /** Seite der jeweils darüberliegenden Ebene, je geöffnetem Knoten ein Eintrag. */
    stapel: number[];
    suchtext: string;
}

export type NavAktion =
    | { typ: 'oeffnen'; id: string }
    | { typ: 'weiter' }
    | { typ: 'zurueck' }
    | { typ: 'modus'; modus: NavModus }
    | { typ: 'suchtext'; text: string }
    | { typ: 'start'; pfad?: string[] };

export const NAV_START: NavZustand = Object.freeze({
    modus: 'torte' as NavModus,
    pfad: [],
    seite: 0,
    stapel: [],
    suchtext: '',
});

/**
 * Ein Schritt zurück; null = schon in der Wurzel auf Seite 1 (der Aufrufer
 * verlässt dann die Auswahl). Reihenfolge: anderer Modus → Torte (Pfad und
 * Seite bleiben) | Seite > 0 → eine Seite zurück | Pfad → eine Ebene hoch
 * auf die gemerkte Seite.
 */
export function navigationZurueck(z: NavZustand): NavZustand | null {
    if (z.modus !== 'torte') return { ...z, modus: 'torte' };
    if (z.seite > 0) return { ...z, seite: z.seite - 1 };
    if (z.pfad.length > 0) {
        const stapel = z.stapel.slice(0, Math.max(0, z.pfad.length - 1));
        const seite = z.stapel.length >= z.pfad.length ? z.stapel[z.pfad.length - 1] : 0;
        return {
            ...z,
            pfad: z.pfad.slice(0, -1),
            seite: Number.isFinite(seite) && seite > 0 ? seite : 0,
            stapel,
        };
    }
    return null;
}

export function navigation(z: NavZustand, a: NavAktion): NavZustand {
    switch (a.typ) {
        case 'oeffnen':
            return { ...z, modus: 'torte', pfad: [...z.pfad, a.id], stapel: [...z.stapel, z.seite], seite: 0 };
        case 'weiter':
            return { ...z, seite: z.seite + 1 };
        case 'zurueck':
            return navigationZurueck(z) ?? z;
        case 'modus':
            return z.modus === a.modus ? z : { ...z, modus: a.modus };
        case 'suchtext':
            return { ...z, modus: 'suche', suchtext: a.text };
        case 'start': {
            const pfad = Array.isArray(a.pfad) ? a.pfad.filter((id) => typeof id === 'string' && id !== '') : [];
            // Ohne Herkunftsseite geht „Zurück“ je Ebene auf Seite 1.
            return { modus: 'torte', pfad, seite: 0, stapel: pfad.map(() => 0), suchtext: '' };
        }
        default:
            return z;
    }
}
